import { useQuery } from '@tanstack/react-query';
import { addDays, todayIso } from './dates';
import { must, useDb } from './db';
import { type LessonItem, fetchLessonById } from './schedule';

/**
 * «Пакет уроку»: everything of one lesson in one place — for whoever missed it (an illness, an
 * alarm, a student abroad learning from the materials): the topic, what to bring, the materials
 * and simulations, the board of the lesson, the homework, a test of the subject coming soon. Read
 * under the usual rules of each table: a student sees the boards the teacher has shown.
 */
export interface LessonPack {
  lesson: LessonItem;
  materials: Array<{
    id: string;
    title: string;
    url: string | null;
    storagePath: string | null;
    mimeType: string | null;
  }>;
  boards: Array<{ id: string; title: string }>;
  homework: Array<{
    id: string;
    title: string;
    body: string | null;
    dueOn: string;
    criteria: string | null;
  }>;
  /** an open test of the subject within the next week */
  test: { id: string; title: string; dueOn: string } | null;
}

export function useLessonPack(lessonId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['lesson-pack', lessonId],
    enabled: lessonId !== '',
    queryFn: async (): Promise<LessonPack | null> => {
      const lesson = await fetchLessonById(db, lessonId);
      if (!lesson) return null;
      const today = todayIso();
      const week = addDays(today, 7);
      const [materials, boards, homework, tests] = await Promise.all([
        db
          .from('lesson_materials')
          .select('id, title, url, storage_path, mime_type')
          .eq('lesson_id', lessonId)
          .order('created_at'),
        db
          .from('boards')
          .select('id, title, shared')
          .eq('lesson_id', lessonId)
          .order('updated_at', { ascending: false }),
        db
          .from('homework')
          .select('id, title, body, due_on, criteria')
          .eq('lesson_id', lessonId)
          .order('due_on'),
        db
          .from('quizzes')
          .select('id, title, due_on')
          .eq('course_id', lesson.courseId)
          .not('published_at', 'is', null)
          .gte('due_on', today)
          .lte('due_on', week)
          .order('due_on')
          .limit(1),
      ]);
      const test = must(tests)[0];
      return {
        lesson,
        materials: must(materials).map((m) => ({
          id: m.id,
          title: m.title,
          url: m.url,
          storagePath: m.storage_path,
          mimeType: m.mime_type,
        })),
        boards: must(boards).map((b) => ({ id: b.id, title: b.title })),
        homework: must(homework).map((h) => ({
          id: h.id,
          title: h.title,
          body: h.body,
          dueOn: h.due_on,
          criteria: h.criteria,
        })),
        test: test?.due_on ? { id: test.id, title: test.title, dueOn: test.due_on } : null,
      };
    },
  });
}
